import { Theme } from '@mui/material';
import { getShadowRoot } from 'src';

/**
 * Convert a RGB color to HSL
 */
function rgbToHsl(r: number, g: number, b: number): string {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Convert a HEX color to HSL
 */
function hexToHsl(hex: string): string {
  // Remove the # if it exists
  hex = hex.replace(/^#/, '');

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return rgbToHsl(r, g, b);
}

/**
 * Extract color from a CSS string (hex, rgb, rgba, hsl, hsla)
 */
function parseColor(color: string): string {
  // If it's hex
  if (color.startsWith('#')) {
    return hexToHsl(color);
  }

  // If it's hsl/hsla - extract the values and return in the correct format
  const hslMatch = color.match(/hsla?\((\d+),\s*(\d+)%,\s*(\d+)%/);
  if (hslMatch) {
    return `${hslMatch[1]} ${hslMatch[2]}% ${hslMatch[3]}%`;
  }

  // If it's rgb/rgba
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    return rgbToHsl(parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3]));
  }

  return '0 0% 0%';
}

// Helper function to apply font family dynamically to Shadow DOM
export function applyDynamicFont(theme: Theme): void {
  const shadowRoot = getShadowRoot();
  if (!shadowRoot) return;

  const fontFamily =
    (theme.typography as any)?.fontFamily ||
    "system-ui, -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

  // Update or create the dynamic font style tag in shadow DOM
  let fontStyleTag = shadowRoot.getElementById('dynamic-font-style') as HTMLStyleElement;
  if (!fontStyleTag) {
    fontStyleTag = document.createElement('style');
    fontStyleTag.id = 'dynamic-font-style';
    shadowRoot.appendChild(fontStyleTag);
  }

  fontStyleTag.textContent = `
    :host {
      --font-family: ${fontFamily};
    }
  `;
}

export function applyTailwindThemeColors(theme: Theme): void {
  const root = document.documentElement;

  applyDynamicFont(theme);

  // Primary
  if (theme.palette.primary.main) {
    root.style.setProperty('--primary', parseColor(theme.palette.primary.main));
  }

  // Secondary
  if (theme.palette.secondary?.main) {
    root.style.setProperty('--secondary', parseColor(theme.palette.secondary.main));
  }

  // Background
  if (theme.palette.background.default) {
    root.style.setProperty('--background', parseColor(theme.palette.background.default));
  }

  if (theme.palette.background.paper) {
    root.style.setProperty('--card', parseColor(theme.palette.background.paper));
  }

  // Text/Foreground
  if (theme.palette.text.primary) {
    root.style.setProperty('--foreground', parseColor(theme.palette.text.primary));
  }

  if (theme.palette.text.secondary) {
    root.style.setProperty('--muted-foreground', parseColor(theme.palette.text.secondary));
  }

  // Error/Destructive
  if (theme.palette.error?.main) {
    root.style.setProperty('--destructive', parseColor(theme.palette.error.main));
  }

  // Divider/Border
  if (theme.palette.divider) {
    root.style.setProperty('--border', parseColor(theme.palette.divider));
    root.style.setProperty('--input', parseColor(theme.palette.divider));
  }

  if (theme.palette.grey?.[100]) {
    root.style.setProperty('--accent', parseColor(theme.palette.grey[100]));
  }

  if (theme.palette.grey?.[200]) {
    root.style.setProperty('--muted', parseColor(theme.palette.grey[200]));
  }

  // Custom palette colors - Brand
  const brandPalette = (theme.palette as any).brand;
  if (brandPalette) {
    const brandShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    brandShades.forEach(shade => {
      if (brandPalette[shade]) {
        root.style.setProperty(`--brand-${shade}`, parseColor(brandPalette[shade]));
      }
    });
  }

  // Custom palette colors - Gray
  const grayPalette = (theme.palette as any).gray;
  if (grayPalette) {
    const grayShades = [25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    grayShades.forEach(shade => {
      if (grayPalette[shade]) {
        root.style.setProperty(`--gray-${shade}`, parseColor(grayPalette[shade]));
      }
    });
  }

  // Custom palette colors - Red
  const redPalette = (theme.palette as any).red;
  if (redPalette) {
    const redShades = [25, 100, 200, 300, 400, 500, 600, 700, 800, 900];
    redShades.forEach(shade => {
      if (redPalette[shade]) {
        root.style.setProperty(`--red-${shade}`, parseColor(redPalette[shade]));
      }
    });
  }

  // Custom palette colors - Pink
  const pinkPalette = (theme.palette as any).pink;
  if (pinkPalette) {
    const pinkShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    pinkShades.forEach(shade => {
      if (pinkPalette[shade]) {
        root.style.setProperty(`--pink-${shade}`, parseColor(pinkPalette[shade]));
      }
    });
  }

  // Custom palette colors - Orange
  const orangePalette = (theme.palette as any).orange;
  if (orangePalette) {
    const orangeShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    orangeShades.forEach(shade => {
      if (orangePalette[shade]) {
        root.style.setProperty(`--orange-${shade}`, parseColor(orangePalette[shade]));
      }
    });
  }

  // Custom palette colors - Yellow
  const yellowPalette = (theme.palette as any).yellow;
  if (yellowPalette) {
    const yellowShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    yellowShades.forEach(shade => {
      if (yellowPalette[shade]) {
        root.style.setProperty(`--yellow-${shade}`, parseColor(yellowPalette[shade]));
      }
    });
  }

  // Custom palette colors - Green
  const greenPalette = (theme.palette as any).green;
  if (greenPalette) {
    const greenShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    greenShades.forEach(shade => {
      if (greenPalette[shade]) {
        root.style.setProperty(`--green-${shade}`, parseColor(greenPalette[shade]));
      }
    });
  }

  // Custom palette colors - Blue
  const bluePalette = (theme.palette as any).blue;
  if (bluePalette) {
    const blueShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    blueShades.forEach(shade => {
      if (bluePalette[shade]) {
        root.style.setProperty(`--blue-${shade}`, parseColor(bluePalette[shade]));
      }
    });
  }

  // Custom palette colors - Cyan
  const cyanPalette = (theme.palette as any).cyan;
  if (cyanPalette) {
    const cyanShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    cyanShades.forEach(shade => {
      if (cyanPalette[shade]) {
        root.style.setProperty(`--cyan-${shade}`, parseColor(cyanPalette[shade]));
      }
    });
  }

  // Custom palette colors - Purple
  const purplePalette = (theme.palette as any).purple;
  if (purplePalette) {
    const purpleShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
    purpleShades.forEach(shade => {
      if (purplePalette[shade]) {
        root.style.setProperty(`--purple-${shade}`, parseColor(purplePalette[shade]));
      }
    });
  }

  // Custom palette colors - Black (opacity variants)
  const blackPalette = (theme.palette as any).black;
  if (blackPalette) {
    const blackOpacities = [
      '100%',
      '87%',
      '72%',
      '52%',
      '32%',
      '24%',
      '16%',
      '12%',
      '6%',
      '4%',
      '2%',
    ];
    blackOpacities.forEach(opacity => {
      if (blackPalette[opacity]) {
        root.style.setProperty(`--black-${opacity}`, parseColor(blackPalette[opacity]));
      }
    });
  }

  // Custom palette colors - White (opacity variants)
  const whitePalette = (theme.palette as any).white;
  if (whitePalette) {
    const whiteOpacities = [
      '100%',
      '87%',
      '72%',
      '52%',
      '32%',
      '24%',
      '16%',
      '12%',
      '6%',
      '4%',
      '2%',
      '0.1%',
    ];
    whiteOpacities.forEach(opacity => {
      if (whitePalette[opacity]) {
        root.style.setProperty(`--white-${opacity}`, parseColor(whitePalette[opacity]));
      }
    });
    if (whitePalette[100]) {
      root.style.setProperty('--white-100', parseColor(whitePalette[100]));
    }
  }

  // Foreground colors
  root.style.setProperty('--primary-foreground', '0 0% 100%');
  root.style.setProperty('--secondary-foreground', '0 0% 100%');
  root.style.setProperty('--destructive-foreground', '0 0% 100%');
  root.style.setProperty('--accent-foreground', parseColor(theme.palette.text.primary));
  root.style.setProperty('--muted-foreground', parseColor(theme.palette.text.secondary));
  root.style.setProperty('--card-foreground', parseColor(theme.palette.text.primary));
  root.style.setProperty('--popover-foreground', parseColor(theme.palette.text.primary));

  // Ring (focus)
  root.style.setProperty('--ring', parseColor(theme.palette.primary.main));
}
